import { resolveSkipTarget } from '../../../surveyLogic/evaluator/evaluateSkipLogics';
import { resolveVisibleSkipTargetPageIndex } from '../../../surveyLogic/navigation/findVisiblePageIndex';
import {
  sanitizePagesAfterIndex,
  sanitizeSkippedPageAnswers,
} from '../../../surveyLogic/navigation/sanitizeSkippedPageAnswers';
import { trackPageSkipped, trackSkipLogicTriggered } from '../../../surveyLogic/telemetry/logicTelemetry';
import { finishCurrentPage } from './finishCurrentPage';
import {
  markAllPagesAfterCurrentSkipped,
  markSkippedPages,
} from './markSkippedPages';
import { navigateToPage } from './navigateToPage';
import type { OnNextRunState } from './types';

/**
 * If the last visible question on this page has skip logic, jump or submit early.
 * Returns true when skip logic handled navigation.
 */
export function trySkipLogicNavigation(run: OnNextRunState): boolean {
  const lastVisibleQuestion = run.currentQuestions[run.currentQuestions.length - 1];
  if (!lastVisibleQuestion?.skipLogics?.length) {
    return false;
  }

  const skipTarget = resolveSkipTarget(
    lastVisibleQuestion,
    run.logicContext,
    run.pages,
    run.currentPageIndex,
    { silent: true }
  );

  if (!skipTarget) {
    return false;
  }

  if (skipTarget.type === 'END_OF_SURVEY') {
    return handleSkipToEndOfSurvey(run, lastVisibleQuestion.id, skipTarget.skipLogicId);
  }

  return handleSkipToQuestion(run, lastVisibleQuestion.id, skipTarget);
}

function handleSkipToEndOfSurvey(
  run: OnNextRunState,
  questionId: string,
  skipLogicId: string
): boolean {
  trackSkipLogicTriggered({
    questionId,
    skipLogicId,
    targetType: 'END_OF_SURVEY',
    targetId: 'END_OF_SURVEY',
    fromPageIndex: run.currentPageIndex,
  });

  const sanitized = sanitizePagesAfterIndex(run.pages, run.values, run.currentPageIndex);
  run.setValues(sanitized);
  markAllPagesAfterCurrentSkipped(run.refs, run.currentPageIndex, run.pages.length);
  finishCurrentPage(run);
  run.setValidationErrors({});
  run.bumpProgressRevision();
  void run.onSubmit({ skipValidation: true, answersOverride: sanitized });
  return true;
}

function handleSkipToQuestion(
  run: OnNextRunState,
  questionId: string,
  skipTarget: Extract<
    NonNullable<ReturnType<typeof resolveSkipTarget>>,
    { type: 'QUESTION' }
  >
): boolean {
  trackSkipLogicTriggered({
    questionId,
    skipLogicId: skipTarget.skipLogicId,
    targetType: 'QUESTION',
    targetId: skipTarget.questionId,
    fromPageIndex: run.currentPageIndex,
  });

  const visibleSkipPageIndex = resolveVisibleSkipTargetPageIndex(
    run.pages,
    skipTarget.pageIndex,
    run.logicContext
  );

  const sanitizeToPageIndex =
    visibleSkipPageIndex >= run.pages.length ? skipTarget.pageIndex : visibleSkipPageIndex;

  const sanitized = sanitizeSkippedPageAnswers(
    run.pages,
    run.values,
    run.currentPageIndex,
    sanitizeToPageIndex
  );
  run.setValues(sanitized);

  if (visibleSkipPageIndex >= run.pages.length) {
    const endSanitized = sanitizePagesAfterIndex(run.pages, sanitized, run.currentPageIndex);
    run.setValues(endSanitized);
    markAllPagesAfterCurrentSkipped(run.refs, run.currentPageIndex, run.pages.length);
    finishCurrentPage(run);
    run.setValidationErrors({});
    run.bumpProgressRevision();
    void run.onSubmit({ skipValidation: true, answersOverride: endSanitized });
    return true;
  }

  markSkippedPages(run.refs, run.currentPageIndex, visibleSkipPageIndex);
  trackPageSkipped({
    fromPageIndex: run.currentPageIndex,
    toPageIndex: visibleSkipPageIndex,
    reason: 'skip_logic',
  });
  finishCurrentPage(run);
  run.setValidationErrors({});
  navigateToPage(run, visibleSkipPageIndex);
  return true;
}
